// NetworkTopology.jsx — Network topology diagram per site (group INFRA).
// Data: GET /api/netmap/sites, GET /api/netmap/site/:key, PUT /api/netmap/site/:key
// Node = aset SDP Cloud (sdp_assets). Link otomatis (simulasi) kecuali site sudah di-edit manual.
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Network, RefreshCw, Download, Pencil, Save, RotateCcw, X, ArrowLeft, Link2, ZoomIn, ZoomOut, Maximize } from "lucide-react";
import AssetDetailDrawer from "../components/AssetDetailDrawer";
import WgDeviceJakarta from "../components/WgDeviceJakarta";
import WgSystemManagerJakarta from "../components/WgSystemManagerJakarta";
import WgTopologyJakarta from "../components/WgTopologyJakarta";
import WgRemoteSite from "../components/WgRemoteSite";
import SiteHealthOverview from "../components/SiteHealthOverview";
import OfficeProbePanel from "./OfficeProbePanel";
import { useAuth } from "../context/AuthContext";
import { allowedLocs } from "../utils/siteScope";
import TunnelHealthBoard, { RawSources } from "./TunnelHealthBoard";

const API_BASE = window.__API_BASE__ || "";
const getToken = () => localStorage.getItem("auth_token") || localStorage.getItem("token") || "";
const api = async (path, opt = {}) => {
  const r = await fetch(`${API_BASE}${path}`, { ...opt, headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}`, ...(opt.headers || {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};

const KIND = {
  internet: { label: "Internet", color: "#0ea5e9", bg: "#e0f2fe" },
  firewall: { label: "Firewall", color: "#dc2626", bg: "#fee2e2" },
  core: { label: "Core Switch", color: "#7c3aed", bg: "#ede9fe" },
  switch: { label: "Switch", color: "#6366f1", bg: "#e0e7ff" },
  ap: { label: "Access Point", color: "#0d9488", bg: "#ccfbf1" },
  vhost: { label: "Virtual Host", color: "#b45309", bg: "#fef3c7" },
  vm: { label: "VM", color: "#a16207", bg: "#fef9c3" },
  server: { label: "Server", color: "#ea580c", bg: "#ffedd5" },
  nas: { label: "NAS", color: "#9333ea", bg: "#f3e8ff" },
  nvr: { label: "DVR/NVR", color: "#475569", bg: "#e2e8f0" },
  pabx: { label: "PABX", color: "#be185d", bg: "#fce7f3" },
  group: { label: "Endpoints", color: "#64748b", bg: "#f1f5f9" },
};
const LINK_STYLE = { wan: { stroke: "#0ea5e9", dash: "6 4", w: 2.5 }, lan: { stroke: "#94a3b8", dash: "", w: 1.6 }, wifi: { stroke: "#14b8a6", dash: "3 3", w: 1.4 }, virtual: { stroke: "#d97706", dash: "2 3", w: 1.2 } };
const NW = 150, NH = 44, GX = 14, GY = 70, LEAF_COLS = 3;

// Tree layout: BFS dari 'inet'; child leaf disusun grid, child non-leaf jadi sub-tree.
function layout(nodes, links) {
  const adj = new Map(nodes.map(n => [n.id, []]));
  links.forEach(l => { adj.get(l.a)?.push(l.b); adj.get(l.b)?.push(l.a); });
  const kids = new Map(), seen = new Set();
  const roots = [nodes.find(n => n.id === "inet") || nodes[0], ...nodes].filter(Boolean);
  for (const r of roots) {
    if (seen.has(r.id)) continue;
    seen.add(r.id); const q = [r.id];
    while (q.length) { const c = q.shift(); kids.set(c, []); for (const nb of adj.get(c) || []) if (!seen.has(nb)) { seen.add(nb); kids.get(c).push(nb); q.push(nb); } }
  }
  const topRoots = []; const childSet = new Set([...kids.values()].flat());
  nodes.forEach(n => { if (!childSet.has(n.id)) topRoots.push(n.id); });
  const width = new Map();
  const W = (id) => {
    if (width.has(id)) return width.get(id);
    const ch = kids.get(id) || [];
    const leaves = ch.filter(c => !(kids.get(c) || []).length), inner = ch.filter(c => (kids.get(c) || []).length);
    let w = inner.reduce((s, c) => s + W(c) + GX, 0);
    if (leaves.length) w += Math.min(leaves.length, LEAF_COLS) * (NW + GX);
    w = Math.max(NW + GX, w); width.set(id, w); return w;
  };
  const pos = {};
  const place = (id, x0, y) => {
    const w = W(id); pos[id] = { x: x0 + w / 2 - NW / 2, y };
    const ch = kids.get(id) || [];
    const leaves = ch.filter(c => !(kids.get(c) || []).length), inner = ch.filter(c => (kids.get(c) || []).length);
    let x = x0 + (w - (inner.reduce((s, c) => s + W(c) + GX, 0) + (leaves.length ? Math.min(leaves.length, LEAF_COLS) * (NW + GX) : 0))) / 2;
    if (leaves.length) {
      const cols = Math.min(leaves.length, LEAF_COLS);
      leaves.forEach((c, i) => { pos[c] = { x: x + (i % cols) * (NW + GX), y: y + NH + GY + Math.floor(i / cols) * (NH + 12) }; });
      x += cols * (NW + GX);
    }
    inner.forEach(c => { place(c, x, y + NH + GY); x += W(c) + GX; });
  };
  let x = 0; topRoots.forEach(r => { place(r, x, 0); x += W(r) + GX * 4; });
  return pos;
}

function bounds(pos) {
  const v = Object.values(pos); if (!v.length) return { x: 0, y: 0, w: 800, h: 400 };
  const xs = v.map(p => p.x), ys = v.map(p => p.y);
  const x = Math.min(...xs) - 40, y = Math.min(...ys) - 40;
  return { x, y, w: Math.max(...xs) + NW + 40 - x, h: Math.max(...ys) + NH + 40 - y };
}

function exportPng(svgEl, name) {
  const clone = svgEl.cloneNode(true);
  const vb = svgEl.viewBox.baseVal; const scale = Math.min(3, 4000 / vb.width);
  clone.setAttribute("width", vb.width * scale); clone.setAttribute("height", vb.height * scale);
  const xml = new XMLSerializer().serializeToString(clone);
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas"); c.width = vb.width * scale; c.height = vb.height * scale;
    const ctx = c.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0, c.width, c.height);
    const a = document.createElement("a"); a.download = `network-topology-${name}.png`; a.href = c.toDataURL("image/png"); a.click();
  };
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
}

const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 };
const btn = (primary) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", border: primary ? "none" : "1px solid #cbd5e1", background: primary ? "rgb(var(--brand-600, 13 148 136))" : "#fff", color: primary ? "#fff" : "#334155" });

// ── Overview: SDP companies grouped by physical site (each site has its own WatchGuard) ──
const SITE_GROUPS = [
  { key: "Medan", re: /medan|palmindo|oleochem/i, wg: "PIM To MEDAN", fw: "WatchGuard + core switch at PAMIN DC (shared)", dc: /medan/i },
  { key: "Dumai", re: /indopalm/i, wg: "PIM To DUMAI", fw: "WatchGuard Dumai" },
  { key: "Kijing", re: /bio/i, wg: "PIM To KIJING", fw: "WatchGuard Kijing" },
  { key: "Jakarta", re: /indomas/i, wg: "", fw: "WatchGuard T80 Jakarta (BOVPN hub)" },
];
const SITE_POS = { Medan: [360, 92], Dumai: [585, 300], Kijing: [360, 512], Jakarta: [135, 300], Other: [585, 512] };
function groupSites(sites) {
  const out = SITE_GROUPS.map((g) => ({ ...g, items: [] }));
  const other = { key: "Other", wg: "", items: [] };
  sites.forEach((s) => { const g = out.find((x) => x.re.test(s.name || "")); (g || other).items.push(s); });
  return out.concat(other.items.length ? [other] : []).filter((g) => g.items.length);
}
function Overview({ sites, onOpen }) {
  const cx = 360, cy = 300, BW = 220, RH = 38;
  const groups = groupSites(sites);
  const short = (n) => String(n || "").replace(/^PT\.?\s*/i, "").slice(0, 26);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)", gap: 16 }} className="netmap-ov">
      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>All Sites — WAN Overview</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>Companies from ManageEngine SDP Cloud grouped by physical site. WAN links are simulated. Click a company to open its diagram.</div>
        <svg viewBox="0 0 720 600" style={{ width: "100%", height: "auto" }}>
          {groups.map((g) => { const [x, y] = SITE_POS[g.key]; return <line key={"l" + g.key} x1={cx} y1={cy} x2={x} y2={y} stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="5 4" />; })}
          <circle cx={cx} cy={cy} r="56" fill="#e0f2fe" stroke="#0ea5e9" strokeWidth="2" />
          <text x={cx} y={cy - 4} textAnchor="middle" fontSize="13" fontWeight="600" fill="#0369a1">Corporate WAN</text>
          <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#0369a1">Internet / VPN</text>
          {groups.map((g) => {
            const [x, y] = SITE_POS[g.key];
            const h = 48 + g.items.length * RH, top = y - h / 2;
            const assets = g.items.reduce((a, s) => a + (s.total || 0), 0);
            return (
              <g key={g.key}>
                <rect x={x - BW / 2} y={top} width={BW} height={h} rx="12" fill="#f5f3ff" stroke="#7c3aed" strokeWidth="1.8" />
                <text x={x - BW / 2 + 12} y={top + 20} fontSize="12.5" fontWeight="700" fill="#4c1d95">{g.key} site</text>
                <text x={x + BW / 2 - 12} y={top + 20} textAnchor="end" fontSize="10" fill="#7c3aed">{g.items.length} co · {assets} assets</text>
                {g.fw && <text x={x - BW / 2 + 12} y={top + 35} fontSize="9" fill="#b91c1c">{g.fw.length > 44 ? g.fw.slice(0, 43) + "…" : g.fw}</text>}
                {g.items.map((s, i) => {
                  const ry = top + 44 + i * RH;
                  return (
                    <g key={s.key} style={{ cursor: "pointer" }} onClick={() => onOpen(s.key)}>
                      <rect x={x - BW / 2 + 8} y={ry} width={BW - 16} height={RH - 6} rx="7" fill="#fff" stroke="#c4b5fd" />
                      <text x={x - BW / 2 + 16} y={ry + 13} fontSize="11" fontWeight="600" fill="#1e293b">{short(s.name)}{g.dc && g.dc.test(s.name) ? " · DC" : ""}</text>
                      <text x={x - BW / 2 + 16} y={ry + 26} fontSize="9.5" fill="#64748b">WG {s.counts.firewall || 0} · SW {s.counts.switch || 0} · AP {s.counts.ap || 0} · {s.total} assets{s.manual ? " · edited" : ""}</text>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
      <div style={{ display: "grid", gap: 14, alignContent: "start" }}>
        {groups.map((g) => (
          <div key={g.key} style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", padding: "0 2px" }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#4c1d95" }}>{g.key} site</span>
              <span style={{ fontSize: 12, color: "#64748b" }}>{g.items.length} {g.items.length > 1 ? "companies" : "company"}{g.fw ? " · " + g.fw : ""}{g.wg ? " · BOVPN " + g.wg : ""}</span>
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}>{g.items.reduce((a, s) => a + (s.total || 0), 0)} assets</span>
            </div>
            {g.items.map((s) => (
              <div key={s.key} style={{ ...card, padding: 12, cursor: "pointer" }} onClick={() => onOpen(s.key)}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}{g.dc && g.dc.test(s.name) && <span style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px", borderRadius: 6, background: "#fee2e2", color: "#b91c1c", verticalAlign: 2 }}>DC · WatchGuard + core switch</span>}</div>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: s.manual ? "#dcfce7" : "#f1f5f9", color: s.manual ? "#166534" : "#64748b", whiteSpace: "nowrap" }}>{s.manual ? "Manual links" : "Auto (simulated)"}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {Object.keys(KIND).filter((k) => s.counts[k]).map((k) => (
                    <span key={k} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: KIND[k].bg, color: KIND[k].color }}>{k === "firewall" ? "WatchGuard" : KIND[k].label} {s.counts[k]}</span>
                  ))}
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: "#f1f5f9", color: "#64748b" }}>Total {s.total}</span>
                </div>
                {g.dc && !g.dc.test(s.name) && <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>No local firewall / core switch — uplinks via WatchGuard and core switch at PAMIN DC</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Site diagram ──
function SiteDiagram({ siteKey, onBack }) {
  const [data, setData] = useState(null), [err, setErr] = useState("");
  const [links, setLinks] = useState([]), [pos, setPos] = useState({});
  const [edit, setEdit] = useState(false), [linkFrom, setLinkFrom] = useState(null), [dirty, setDirty] = useState({ pos: false, links: false });
  const [assetRow, setAssetRow] = useState(null);
  const [sel, setSel] = useState(null), [vb, setVb] = useState(null), [q, setQ] = useState(""), [busy, setBusy] = useState(false);
  const svgRef = useRef(null), drag = useRef(null);

  const load = useCallback(async () => {
    setErr(""); setData(null);
    try {
      const d = await api(`/api/netmap/site/${encodeURIComponent(siteKey)}`);
      const auto = layout(d.nodes, d.links);
      const p = { ...auto }; Object.entries(d.positions || {}).forEach(([k, v]) => { if (p[k]) p[k] = v; });
      setData(d); setLinks(d.links); setPos(p); setVb(bounds(p)); setDirty({ pos: false, links: false }); setEdit(false); setLinkFrom(null);
    } catch (e) { setErr(e.message); }
  }, [siteKey]);
  useEffect(() => { load(); }, [load]);

  const nodeMap = useMemo(() => new Map((data?.nodes || []).map(n => [n.id, n])), [data]);
  const match = useMemo(() => { const s = q.trim().toLowerCase(); if (!s || !data) return null; return new Set(data.nodes.filter(n => [n.label, n.asset?.ip_address, n.asset?.location].join(" ").toLowerCase().includes(s)).map(n => n.id)); }, [q, data]);

  const toSvg = (e) => { const pt = svgRef.current.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY; return pt.matrixTransform(svgRef.current.getScreenCTM().inverse()); };
  const onDown = (e, id) => {
    e.stopPropagation();
    const p = toSvg(e);
    if (id && edit) drag.current = { id, dx: p.x - pos[id].x, dy: p.y - pos[id].y, moved: false };
    else if (!id) drag.current = { pan: true, sx: e.clientX, sy: e.clientY, vb0: vb };
  };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    if (d.pan) { const k = vb.w / svgRef.current.clientWidth; setVb({ ...d.vb0, x: d.vb0.x - (e.clientX - d.sx) * k, y: d.vb0.y - (e.clientY - d.sy) * k }); return; }
    const p = toSvg(e); d.moved = true; setPos(o => ({ ...o, [d.id]: { x: Math.round(p.x - d.dx), y: Math.round(p.y - d.dy) } })); setDirty(x => ({ ...x, pos: true }));
  };
  const onUp = () => { drag.current = null; };
  const zoom = (f) => setVb(v => { const w = v.w * f, h = v.h * f; return { x: v.x + (v.w - w) / 2, y: v.y + (v.h - h) / 2, w, h }; });
  const onWheel = (e) => { e.preventDefault(); zoom(e.deltaY > 0 ? 1.12 : 0.89); };
  useEffect(() => { const el = svgRef.current; if (!el) return; el.addEventListener("wheel", onWheel, { passive: false }); return () => el.removeEventListener("wheel", onWheel); });

  const clickNode = (id) => {
    if (drag.current?.moved) return;
    if (edit && linkFrom) {
      if (linkFrom !== id && !links.some(l => (l.a === linkFrom && l.b === id) || (l.a === id && l.b === linkFrom))) { setLinks(ls => [...ls, { a: linkFrom, b: id, type: nodeMap.get(linkFrom)?.kind === "internet" ? "wan" : "lan" }]); setDirty(x => ({ ...x, links: true })); }
      setLinkFrom(null); return;
    }
    setSel(id);
    if (!edit) { const nn = nodeMap.get(id); if (nn && nn.asset && nn.asset.id) setAssetRow(nn.asset); }
  };
  const removeLink = (i) => { if (!edit) return; setLinks(ls => ls.filter((_, j) => j !== i)); setDirty(x => ({ ...x, links: true })); };
  const save = async () => {
    setBusy(true);
    try { await api(`/api/netmap/site/${encodeURIComponent(siteKey)}`, { method: "PUT", body: JSON.stringify({ positions: pos, ...(dirty.links || data.mode === "manual" ? { links } : {}) }) }); await load(); }
    catch (e) { alert("Save failed: " + e.message); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset this site to the automatic (simulated) topology? Manual links and positions will be removed.")) return;
    setBusy(true); try { await api(`/api/netmap/site/${encodeURIComponent(siteKey)}`, { method: "PUT", body: JSON.stringify({ reset: true }) }); await load(); } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  if (err) return <div style={card}><button style={btn()} onClick={onBack}><ArrowLeft size={14} /> Back</button><div style={{ color: "#dc2626", marginTop: 12 }}>{err}</div></div>;
  if (!data || !vb) return <div style={card}>Loading diagram…</div>;
  const selNode = sel ? nodeMap.get(sel) : null;
  const counts = {}; data.nodes.forEach(n => { counts[n.kind] = (counts[n.kind] || 0) + 1; });

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ ...card, padding: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <button style={btn()} onClick={onBack}><ArrowLeft size={14} /> All sites</button>
        <div style={{ fontWeight: 600, fontSize: 15, marginRight: "auto" }}>{data.name}
          <span style={{ marginLeft: 8, fontSize: 11, padding: "2px 8px", borderRadius: 99, background: data.mode === "manual" ? "#dcfce7" : "#fef3c7", color: data.mode === "manual" ? "#166534" : "#92400e" }}>{data.mode === "manual" ? `Manual links${data.updated_by ? " · " + data.updated_by : ""}` : "Auto links (simulated)"}</span>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find name / IP / location" style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, minWidth: 180 }} />
        <button style={btn()} onClick={() => zoom(0.8)} title="Zoom in"><ZoomIn size={14} /></button>
        <button style={btn()} onClick={() => zoom(1.25)} title="Zoom out"><ZoomOut size={14} /></button>
        <button style={btn()} onClick={() => setVb(bounds(pos))} title="Fit"><Maximize size={14} /></button>
        <button style={btn()} onClick={() => exportPng(svgRef.current, siteKey)}><Download size={14} /> PNG</button>
        {!edit && <button style={btn()} onClick={() => setEdit(true)}><Pencil size={14} /> Edit</button>}
        {edit && <>
          <button style={{ ...btn(), ...(linkFrom ? { borderColor: "#0d9488", color: "#0d9488" } : {}) }} onClick={() => setLinkFrom(linkFrom ? null : (sel || "pick"))}><Link2 size={14} /> {linkFrom ? (linkFrom === "pick" ? "Click source node…" : "Click target node…") : "Add link"}</button>
          <button style={btn(true)} disabled={busy || (!dirty.pos && !dirty.links)} onClick={save}><Save size={14} /> Save</button>
          <button style={btn()} onClick={load}><X size={14} /> Cancel</button>
          <button style={btn()} onClick={reset}><RotateCcw size={14} /> Reset to auto</button>
        </>}
      </div>
      {edit && <div style={{ fontSize: 12, color: "#64748b", marginTop: -4 }}>Edit mode: drag nodes to move · "Add link" then click two nodes · click a line to delete it · Save stores links for this site (switches it to manual mode).</div>}

      <div style={{ display: "grid", gridTemplateColumns: selNode ? "minmax(0,1fr) 300px" : "minmax(0,1fr)", gap: 12 }} className="netmap-site">
        <div style={{ ...card, padding: 0, overflow: "hidden", position: "relative" }}>
          <svg ref={svgRef} viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} style={{ width: "100%", height: "70vh", display: "block", background: "#f8fafc", cursor: drag.current?.pan ? "grabbing" : "grab", touchAction: "none" }}
            onPointerDown={e => onDown(e, null)} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} xmlns="http://www.w3.org/2000/svg">
            <rect x={vb.x - 10000} y={vb.y - 10000} width="30000" height="30000" fill="#f8fafc" />
            {links.map((l, i) => {
              const a = pos[l.a], b = pos[l.b]; if (!a || !b) return null; const s = LINK_STYLE[l.type] || LINK_STYLE.lan;
              const x1 = a.x + NW / 2, y1 = a.y + NH, x2 = b.x + NW / 2, y2 = b.y; const my = (y1 + y2) / 2;
              const d = y2 > y1 ? `M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}` : `M${x1},${a.y + NH / 2} L${x2},${b.y + NH / 2}`;
              const dim = match && !(match.has(l.a) || match.has(l.b));
              return <g key={i} onClick={() => removeLink(i)} style={{ cursor: edit ? "pointer" : "default" }}>
                <path d={d} fill="none" stroke="transparent" strokeWidth="10" />
                <path d={d} fill="none" stroke={s.stroke} strokeWidth={s.w} strokeDasharray={s.dash} opacity={dim ? 0.15 : 0.85} />
              </g>;
            })}
            {data.nodes.map(n => {
              const p = pos[n.id]; if (!p) return null; const k = KIND[n.kind] || KIND.group;
              const dim = match && !match.has(n.id); const isSel = sel === n.id || linkFrom === n.id;
              return (
                <g key={n.id} transform={`translate(${p.x},${p.y})`} opacity={dim ? 0.25 : 1} style={{ cursor: edit ? "move" : "pointer" }}
                  onPointerDown={e => onDown(e, n.id)} onClick={(e) => { e.stopPropagation(); if (linkFrom === "pick") { setLinkFrom(n.id); return; } clickNode(n.id); }}>
                  <rect width={NW} height={NH} rx="8" fill={k.bg} stroke={isSel ? "#0f172a" : k.color} strokeWidth={isSel ? 2.5 : 1.3} />
                  <rect width="6" height={NH} rx="3" fill={k.color} />
                  <text x="14" y="17" fontSize="11.5" fontWeight="600" fill="#0f172a">{String(n.label || "").slice(0, 20)}</text>
                  <text x="14" y="33" fontSize="10" fill={k.color}>{k.label}{n.asset?.ip_address ? " · " + n.asset.ip_address : ""}</text>
                </g>
              );
            })}
          </svg>
          <div style={{ position: "absolute", left: 10, bottom: 10, background: "rgba(255,255,255,.92)", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 10px", fontSize: 11, display: "flex", flexWrap: "wrap", gap: 10, maxWidth: "calc(100% - 20px)" }}>
            {Object.keys(counts).map(k => <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: KIND[k]?.color }} />{KIND[k]?.label} {counts[k]}</span>)}
            {data.ups ? <span>UPS {data.ups} (not drawn)</span> : null}
            <span style={{ color: "#0ea5e9" }}>– – WAN</span><span style={{ color: "#14b8a6" }}>··· Wi-Fi</span><span style={{ color: "#d97706" }}>··· Virtual</span>
          </div>
        </div>
        {assetRow && <AssetDetailDrawer row={assetRow} onClose={() => setAssetRow(null)} />}
        {selNode && (
          <div style={{ ...card, alignSelf: "start" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 8 }}>
              <div><div style={{ fontWeight: 600 }}>{selNode.label}</div><div style={{ fontSize: 12, color: KIND[selNode.kind]?.color }}>{KIND[selNode.kind]?.label}</div></div>
              <button style={{ ...btn(), padding: 4 }} onClick={() => setSel(null)}><X size={14} /></button>
            </div>
            {selNode.asset ? (
              <table style={{ width: "100%", fontSize: 12, marginTop: 10, borderCollapse: "collapse" }}><tbody>
                {[["Product", selNode.asset.product_name], ["Type", selNode.asset.product_type], ["Vendor", selNode.asset.manufacturer], ["IP", selNode.asset.ip_address], ["MAC", selNode.asset.mac_address], ["Serial", selNode.asset.serial_number], ["Location", selNode.asset.location], ["Department", selNode.asset.department], ["User", selNode.asset.assigned_user], ["State", selNode.asset.state]].map(([k, v]) => (
                  <tr key={k}><td style={{ color: "#64748b", padding: "4px 8px 4px 0", verticalAlign: "top", whiteSpace: "nowrap" }}>{k}</td><td style={{ padding: "4px 0", wordBreak: "break-word" }}>{v || "—"}</td></tr>
                ))}</tbody></table>
            ) : <div style={{ fontSize: 12, color: "#64748b", marginTop: 10 }}>{selNode.kind === "group" ? `${selNode.count} endpoint assets grouped (not drawn individually).` : "Logical node (not an SDP asset)."}</div>}
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 10 }}>Connected to: {links.filter(l => l.a === selNode.id || l.b === selNode.id).map(l => nodeMap.get(l.a === selNode.id ? l.b : l.a)?.label).filter(Boolean).slice(0, 12).join(", ") || "—"}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Firewall Device tab: one sub-tab per site firewall. Jakarta = live SNMP of the T80; the others have no direct access (WgRemoteSite).
const FW_SITES = [["jakarta", "Jakarta Site"], ["medan", "Medan Site"], ["dumai", "Dumai Site"], ["kijing", "Kijing Site"]];

export default function NetworkTopology() {
  const { user } = useAuth();
  const locs = allowedLocs(user);
  const fwSites = FW_SITES.filter(([k]) => !locs || locs.includes(k));
  const [tab, setTab] = useState(() => { const q = new URLSearchParams(window.location.search); return q.get("site") ? "sites" : (["overview", "tunnels", "devices", "firewall", "sites"].includes(q.get("tab")) ? q.get("tab") : "overview"); });
  const [fwSite, setFwSite] = useState(() => { const f = new URLSearchParams(window.location.search).get("fw"); const fs = FW_SITES.filter(([k]) => !locs || locs.includes(k)); return fs.some(([k]) => k === f) ? f : (fs[0] || FW_SITES[0])[0]; });
  const [sites, setSites] = useState(null), [err, setErr] = useState(""), [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("site") || null);
  const load = useCallback(() => { setErr(""); api("/api/netmap/sites").then(d => setSites(d.sites)).catch(e => setErr(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  const go = (k) => { setOpen(k); const u = new URL(window.location.href); if (k) u.searchParams.set("site", k); else u.searchParams.delete("site"); window.history.replaceState(null, "", u.toString()); };
  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      <style>{`@media (max-width: 900px){ .netmap-ov, .netmap-site { grid-template-columns: minmax(0,1fr) !important; } }`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ marginRight: "auto" }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>Network Topology</h1>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>Site health, WAN tunnels, site device probes, firewall devices and SDP asset diagrams for all Pacific sites.</div>
        </div>
        {!open && tab === "sites" && <button style={{ ...btn(), background: "#fff", color: "rgb(var(--brand-700))", border: 0 }} onClick={load}><RefreshCw size={14} /> Refresh</button>}
      </div>
      {tab === "sites" && err && <div style={{ ...card, color: "#dc2626" }}>{err}</div>}
      {tab === "sites" && !err && !sites && <div style={card}>Loading sites…</div>}
      {!open && <div style={{ display: "flex", gap: 6, borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>{[["overview", "Overview"], ["tunnels", "Tunnels & WAN"], ["devices", "Site Devices"], ["firewall", "Firewall Device"], ["sites", "Devices & Assets"]].map(([k, l]) => (<button key={k} onClick={() => setTab(k)} style={{ padding: "8px 14px", border: 0, background: "transparent", cursor: "pointer", fontWeight: 600, fontSize: 13, color: tab === k ? "rgb(var(--brand-700, 15 118 110))" : "#64748b", borderBottom: tab === k ? "2px solid rgb(var(--brand-600, 13 148 136))" : "2px solid transparent", marginBottom: -1 }}>{l}</button>))}</div>}
      {tab === "overview" && !open && <SiteHealthOverview onGo={setTab} />}
      {tab === "tunnels" && !open && <div style={{ display: "grid", gap: 16 }}><TunnelHealthBoard /><RawSources /></div>}
      {tab === "devices" && !open && <OfficeProbePanel />}
      {tab === "firewall" && !open && (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, padding: 4, background: "#f1f5f9", borderRadius: 12, justifySelf: "start" }}>
            {fwSites.map(([k, l]) => (<button key={k} onClick={() => setFwSite(k)} style={{ padding: "6px 14px", border: 0, borderRadius: 9, cursor: "pointer", fontWeight: 600, fontSize: 12.5, background: fwSite === k ? "#fff" : "transparent", color: fwSite === k ? "rgb(var(--brand-700, 15 118 110))" : "#64748b", boxShadow: fwSite === k ? "0 1px 2px rgba(15,23,42,.08), 0 0 0 1px #e2e8f0" : "none" }}>{l}</button>))}
          </div>
          {fwSite === "jakarta" && <><WgDeviceJakarta /><WgTopologyJakarta /><WgSystemManagerJakarta /></>}
          {fwSite !== "jakarta" && <WgRemoteSite key={fwSite} siteKey={fwSite} />}
        </div>
      )}
      {tab === "sites" && sites && (open ? <SiteDiagram key={open} siteKey={open} onBack={() => { go(null); load(); }} /> : <Overview sites={sites} onOpen={go} />)}
    </div>
  );
}
